import * as React from 'react';
import { useSelector } from 'react-redux';
import { getReleaseTypeForNonBlockingSteps, isNonBlockingStepForReleaseType, isReleaseStepType } from '../../helpers';
import { RootState } from '../../reducers/reducers';
import {
    createFeatureFlagsRoute,
    createReleaseCalendarRoute,
    createReleaseOverviewRoute,
    createReleaseRolloutRoute,
    createReleaseStepRoute,
    createReleaseStepTabRoute,
    createStepRunRoute,
    createStepRunTabRoute,
    ReleaseStepTabType,
    createTimelineEventsRoute,
    createStepRunsSummaryRoute,
} from '../../helpers/routeHelpers';
import { ReleaseStatus, ReleaseStepType, ReleaseType, SubscriptionFeatureType } from '../../types';
import { antGrey, hexStringToRGBAString, runwayColors, titleStyle } from '../styles/globalStyles';
import { ReleaseComponentProps } from '../../typescriptHelpers';
import ReleaseVersion from '../ReleaseVersionComponent';
import {
    selectApp,
    selectHasVCSIntegrationConnected,
    selectIsAppsDemoModeEnabled,
    selectIsDemoModeEnabledForRelease,
    selectIsReleaseFromScraper,
    selectOrg,
    selectRelease,
    selectReleaseStepsBlockingConfigForReleaseType,
    selectIsRolloutPaused,
    selectShouldHideNonBlockingSteps,
    selectStepRunGraph,
    selectWorkflowEnabledSteps,
    selectIsWorkflowsCIStepEnabled,
    selectIsWorkflowsSSRStepsEnabled,
    selectRolloutStepRunStatusDataInStepRunGraph,
    selectDestinationInfoByDestinationStepConfigId,
    selectUserIsRunwayAdmin,
} from '../../selectors/selectors';
import { CalendarFilled, ClockCircleFilled, DashboardFilled, FlagFilled, SignalFilled } from '@ant-design/icons';
import { ReleaseStepsSidebarNode } from './ReleaseStepsSidebarNode';
import AdditionalItem from './StepAdditionalItem';
import { RolloutBadge } from './RolloutBadge';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import Text from 'antd/lib/typography/Text';
import { matchPath, useLocation } from 'react-router-dom';
import { DestinationStatusDataSourceItem } from '../workflowOverview/widgets/WorkflowRunDestinationStatus';
import { displayTextForOverviewDestinationStatus } from '@/helpers/displayHelpers';

const defaultStepTabForStep: (stepType: ReleaseStepType) => ReleaseStepTabType = (stepType) => {
    switch (stepType) {
        default:
            return ReleaseStepTabType.Summary;
    }
};

const SUPPORTED_SUMMARY_ARCHETYPES = [];

export const ReleaseStepsSidebar: React.FC<ReleaseComponentProps> = (props) => {
    const { orgId, appId, releaseId } = props;

    const location = useLocation();

    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const app = useSelector((state: RootState) => selectApp(state, appId));
    const release = useSelector((state: RootState) => selectRelease(state, releaseId));
    const hasVCSConnected = useSelector((state: RootState) => selectHasVCSIntegrationConnected(state, appId));
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));
    const destinationsByStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );

    const stepRunGraph = useSelector((state: RootState) => selectStepRunGraph(state, releaseId));
    const releaseIsRollback = release.type === ReleaseType.Rollback;

    const isTagNeeded =
        release ?
            release.status === ReleaseStatus.Completed && release.isReleaseTagged === false && hasVCSConnected
        :   false;

    const isReleaseDemoModeEnabled = useSelector((state: RootState) =>
        selectIsDemoModeEnabledForRelease(state, releaseId),
    );
    const isAppsDemoModeEnabled = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const showReleaseDemoModeBanner = isReleaseDemoModeEnabled && !isAppsDemoModeEnabled;
    const isRolloutPaused = useSelector((state: RootState) => selectIsRolloutPaused(state, appId, releaseId));
    const isReleaseCreatedFromScraper = useSelector((state: RootState) => selectIsReleaseFromScraper(state, releaseId));

    const shouldHideNonBlockingSteps = useSelector((state: RootState) =>
        selectShouldHideNonBlockingSteps(state, appId),
    );
    const nonBlockingStepsConfigMap = useSelector((state: RootState) =>
        selectReleaseStepsBlockingConfigForReleaseType(state, appId),
    );

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));
    const isWorkflowsSSRStepsEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsSSRStepsEnabled(state, appId),
    );

    const rolloutStepRunStatusDataInStepRunGraph = useSelector((state: RootState) =>
        selectRolloutStepRunStatusDataInStepRunGraph(state, releaseId),
    );

    const isRunwayAdmin = useSelector((state: RootState) => selectUserIsRunwayAdmin(state));

    const statusDataSources: DestinationStatusDataSourceItem[] = rolloutStepRunStatusDataInStepRunGraph.map(
        (rolloutStepRunStatusData) => {
            const extraData = rolloutStepRunStatusData.extraData;
            const destination = extraData?.destination;
            const status = extraData?.status;
            const phasedReleasePercentage = extraData?.phasedReleasePercentage;
            const maybeReviewStartedAt = extraData?.maybeReviewStartedAt;

            return {
                destinationStepId: destination?.stepConfigId,
                destination: destination,
                status: status,
                displayText: displayTextForOverviewDestinationStatus(
                    status,
                    destination?.platform,
                    destination?.appStoreType,
                    phasedReleasePercentage,
                ),
                maybeReviewStartedAt: maybeReviewStartedAt,
            };
        },
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <ReleaseVersion {...props} statusDataSources={statusDataSources} />
            {showReleaseDemoModeBanner && (
                <div
                    style={{
                        backgroundColor: hexStringToRGBAString(runwayColors.primary, 0.85),
                        color: runwayColors.white,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    This release is in demo mode.
                </div>
            )}
            <div style={{ flexGrow: 1 }}>
                <div style={{ paddingTop: 12, paddingBottom: 12 }}>
                    <ul style={{ listStyle: 'none', margin: '0px', padding: '0px' }}>
                        {!releaseIsRollback && (
                            <AdditionalItem
                                itemText={'Overview'}
                                route={createReleaseOverviewRoute(org.orgId, app.appId, release.releaseId)}
                                icon={DashboardFilled}
                            />
                        )}
                        {!releaseIsRollback && (
                            <AdditionalItem
                                itemText={'Timeline'}
                                route={createTimelineEventsRoute(org.orgId, app.appId, release.releaseId)}
                                icon={ClockCircleFilled}
                            />
                        )}
                        {!releaseIsRollback && (
                            <AdditionalItem
                                itemText={'Schedule'}
                                route={createReleaseCalendarRoute(org.orgId, app.appId, release.releaseId)}
                                icon={CalendarFilled}
                            />
                        )}
                        {!isWorkflowsSSRStepsEnabled && !workflowEnabledSteps[ReleaseStepType.Rollout] && (
                            <AdditionalItem
                                leftNode={<RolloutBadge isRolloutPaused={isRolloutPaused} />}
                                itemText={'Rollout'}
                                route={createReleaseRolloutRoute(org.orgId, app.appId, release.releaseId)}
                                icon={SignalFilled}
                            />
                        )}
                        {!releaseIsRollback && (
                            <AdditionalItem
                                itemText={'Feature flags'}
                                route={createFeatureFlagsRoute(org.orgId, app.appId, release.releaseId)}
                                icon={FlagFilled}
                            />
                        )}
                    </ul>
                </div>
            </div>
            <div style={{ flexGrow: 1, paddingTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingLeft: 20, paddingRight: 20 }}>
                    <Text style={{ ...titleStyle, fontSize: 12, color: antGrey.grey7 }}>Release steps</Text>
                </div>
                {stepRunGraph.stepGroups.map((group, groupKeyIndex) => {
                    const isLastGroup = groupKeyIndex === stepRunGraph.stepGroups.length - 1;

                    // filtered first so that we can get the correct index for the group and determine if it is the last/first node
                    const filteredArchetypeGroups = group.stepArchetypeGroups.filter((group) => {
                        // Since rollout is not an actual step for non-WF/legacy apps, we hide it if SSR is not enabled
                        if (
                            group.archetype === ReleaseStepType.Rollout &&
                            !isWorkflowsSSRStepsEnabled &&
                            !workflowEnabledSteps[ReleaseStepType.Rollout]
                        ) {
                            return false;
                        }

                        if (shouldHideNonBlockingSteps) {
                            return !isNonBlockingStepForReleaseType(
                                nonBlockingStepsConfigMap[getReleaseTypeForNonBlockingSteps(release.type)],
                                group.archetype,
                            );
                        }

                        return true;
                    });

                    const massagedFilteredArchetypeGroups = filteredArchetypeGroups.map(
                        (archetypeGroup, stepArchetypeIndex) => {
                            const isLastNode = stepArchetypeIndex === filteredArchetypeGroups.length - 1;
                            const isFirstNode = stepArchetypeIndex === 0;

                            const isNonBlocking = isNonBlockingStepForReleaseType(
                                nonBlockingStepsConfigMap[getReleaseTypeForNonBlockingSteps(release.type)],
                                archetypeGroup.archetype,
                            );

                            const isTagNeededForStep = isTagNeeded && isReleaseStepType(archetypeGroup.archetype);

                            const orgId = org.orgId;
                            const appId = app.appId;
                            const releaseId = release.releaseId;
                            const stepArchetype = archetypeGroup.archetype;

                            const allStepRunPaths = archetypeGroup.stepRunStatusDatum.map((stepRunStatusData) => {
                                if (!stepRunStatusData.stepRunId) {
                                    return createReleaseStepRoute(orgId, appId, releaseId, stepArchetype);
                                }

                                return createStepRunRoute(orgId, appId, releaseId, stepRunStatusData.stepRunId);
                            });

                            const allStepRunTabPaths = archetypeGroup.stepRunStatusDatum.map((stepRunStatusData) => {
                                if (!stepRunStatusData.stepRunId) {
                                    return createReleaseStepTabRoute(
                                        orgId,
                                        appId,
                                        releaseId,
                                        stepArchetype,
                                        defaultStepTabForStep(stepArchetype),
                                    );
                                }

                                return createStepRunTabRoute(
                                    orgId,
                                    appId,
                                    releaseId,
                                    stepRunStatusData.stepRunId,
                                    defaultStepTabForStep(stepArchetype),
                                );
                            });

                            const doesMatchAtLeastOnePath = !!matchPath(location.pathname, {
                                path: allStepRunPaths,
                            });

                            const doesMatchTheStepRunSummaryPath = !!matchPath(location.pathname, {
                                path: createStepRunsSummaryRoute(
                                    orgId,
                                    appId,
                                    releaseId,
                                    groupKeyIndex,
                                    stepArchetypeIndex,
                                ),
                            });

                            return {
                                ...archetypeGroup,
                                isLastNode,
                                isFirstNode,
                                isNonBlocking,
                                isTagNeededForStep,
                                doesMatchAtLeastOnePath,
                                doesMatchTheStepRunSummaryPath,
                                allStepRunTabPaths,
                                allStepRunPaths,
                            };
                        },
                    );

                    return (
                        <div
                            style={
                                isLastGroup ?
                                    { paddingTop: 12, paddingBottom: 12, backgroundColor: antGrey.grey3 }
                                :   { paddingTop: 12, paddingBottom: 12 }
                            }
                            key={groupKeyIndex}
                        >
                            {isLastGroup && (
                                <div
                                    style={{
                                        width: 0,
                                        height: 0,
                                        borderLeft: '12px solid transparent',
                                        borderRight: '12px solid transparent',
                                        borderTop: '12px solid',
                                        borderTopColor: antGrey.grey3,
                                        position: 'relative',
                                        top: -12,
                                        left: 'calc(100% - 40px)',
                                    }}
                                ></div>
                            )}
                            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                                {massagedFilteredArchetypeGroups.map(
                                    (
                                        {
                                            archetype,
                                            isLastNode,
                                            isFirstNode,
                                            isNonBlocking,
                                            isTagNeededForStep,
                                            stepRunStatusDatum,
                                            doesMatchAtLeastOnePath,
                                            doesMatchTheStepRunSummaryPath,
                                            allStepRunPaths,
                                            allStepRunTabPaths,
                                            combinedStatus,
                                            name,
                                        },
                                        stepArchetypeIndex,
                                    ) => {
                                        const possiblePreviousNode =
                                            massagedFilteredArchetypeGroups[stepArchetypeIndex - 1];
                                        const previousNodeHasSubStepsAndIsExpanded =
                                            possiblePreviousNode?.doesMatchAtLeastOnePath &&
                                            possiblePreviousNode?.stepRunStatusDatum.length > 1;

                                        const stepHasMultipleStepRuns = stepRunStatusDatum.length > 1;

                                        const shouldShowStepRunSummary =
                                            stepHasMultipleStepRuns &&
                                            (SUPPORTED_SUMMARY_ARCHETYPES.includes(archetype) ||
                                                process.env.NODE_ENV === 'development' ||
                                                isRunwayAdmin);

                                        const stepConfigIds = stepRunStatusDatum.map((stepRun) => stepRun.stepConfigId);
                                        const stepDestinations = stepConfigIds
                                            .map((stepConfigId) => destinationsByStepConfigId[stepConfigId])
                                            .filter(Boolean);

                                        const appPlatforms = stepDestinations.map((dest) => dest.platform);
                                        const appStoreTypes = stepDestinations.map((dest) => dest.appStoreType);

                                        return (
                                            <li key={stepArchetypeIndex + archetype}>
                                                <PlanRestrictedFeatureWrapper
                                                    feature={SubscriptionFeatureType.ApprovalsStep}
                                                >
                                                    {(isApprovalsStepRestricted) => (
                                                        <ReleaseStepsSidebarNode
                                                            workflowEnabledSteps={workflowEnabledSteps}
                                                            previousNodeHasSubStepsAndIsExpanded={
                                                                previousNodeHasSubStepsAndIsExpanded
                                                            }
                                                            stepRunStatusDatum={stepRunStatusDatum}
                                                            stepRunsSummaryRoute={
                                                                shouldShowStepRunSummary ?
                                                                    createStepRunsSummaryRoute(
                                                                        org.orgId,
                                                                        app.appId,
                                                                        release.releaseId,
                                                                        groupKeyIndex,
                                                                        stepArchetypeIndex,
                                                                    )
                                                                :   undefined
                                                            }
                                                            isTagNeeded={isTagNeededForStep}
                                                            isFirstNode={isFirstNode}
                                                            isLastNode={isLastNode}
                                                            isNonBlocking={isNonBlocking}
                                                            allStepRunPaths={allStepRunPaths}
                                                            allStepRunTabPaths={allStepRunTabPaths}
                                                            stepArchetype={archetype}
                                                            isActive={
                                                                shouldShowStepRunSummary ?
                                                                    doesMatchTheStepRunSummaryPath
                                                                :   doesMatchAtLeastOnePath
                                                            }
                                                            appPlatform={appPlatforms}
                                                            appStoreType={appStoreTypes}
                                                            isPlanRestricted={
                                                                isApprovalsStepRestricted &&
                                                                archetype === ReleaseStepType.Approvals
                                                            }
                                                            isFromScraper={isReleaseCreatedFromScraper}
                                                            isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                                                            combinedStatus={combinedStatus}
                                                            customArchetypeGroupName={name}
                                                        />
                                                    )}
                                                </PlanRestrictedFeatureWrapper>
                                            </li>
                                        );
                                    },
                                )}
                            </ul>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
